import React from 'react';
import type {
  TGuaranteesContractOption,
  TGuaranteesRootSectionFormValues,
  TGuaranteesSecretSectionSaveContext,
} from '../../rootSection/types.ts';
import { useApplicationRequestActions } from '../../shared/hooks/useApplicationRequestActions.ts';
import type { TUseApplicationRequestActionsParams } from '../../shared/hooks/useApplicationRequestActions.ts';

type TUseClientApplicationActionsParams = TUseApplicationRequestActionsParams & {
  isClientReadOnly: boolean;
};

export const useClientApplicationActions = ({
  isClientReadOnly,
  ...baseParams
}: TUseClientApplicationActionsParams) => {
  const baseActions = useApplicationRequestActions(baseParams);

  const handleSaveRootSection = async (
    values: TGuaranteesRootSectionFormValues
  ): Promise<boolean> => {
    if (isClientReadOnly) {
      return false;
    }

    return baseActions.handleSaveRootSection(values);
  };

  const handleRootFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ): Promise<boolean> => {
    let isSaved = false;

    await baseParams.handleSubmit(async (values) => {
      isSaved = await handleSaveRootSection(values);
    })(event);

    return isSaved;
  };

  const guardVoidAction =
    <TArgs extends unknown[]>(action: (...args: TArgs) => Promise<void>) =>
    async (...args: TArgs) => {
      if (isClientReadOnly) {
        return;
      }

      await action(...args);
    };

  const guardBooleanAction =
    <TArgs extends unknown[]>(action: (...args: TArgs) => Promise<boolean>) =>
    async (...args: TArgs) => {
      if (isClientReadOnly) {
        return false;
      }

      return action(...args);
    };

  const handleSaveSecret = async (
    values: TGuaranteesRootSectionFormValues,
    context: TGuaranteesSecretSectionSaveContext
  ) => {
    if (isClientReadOnly) {
      return false;
    }

    return baseActions.handleSaveSecret(values, context);
  };

  const handleMainDealSelect = async (option: TGuaranteesContractOption): Promise<boolean> => {
    if (isClientReadOnly) {
      return false;
    }

    return baseActions.handleMainDealSelect(option);
  };

  return {
    ...baseActions,
    handleCheckApplicationRequest: guardVoidAction(baseActions.handleCheckApplicationRequest),
    handleMainDealSelect,
    handleRootFormSubmit,
    handleSaveBaseRelations: guardBooleanAction(baseActions.handleSaveBaseRelations),
    handleSaveBeneficiary: guardBooleanAction(baseActions.handleSaveBeneficiary),
    handleSaveGuaranteeForm: guardBooleanAction(baseActions.handleSaveGuaranteeForm),
    handleSavePrincipal: guardBooleanAction(baseActions.handleSavePrincipal),
    handleSaveRootSection,
    handleSaveSecret,
    handleSearchTender: guardVoidAction(baseActions.handleSearchTender),
    handleSelectTender: guardBooleanAction(baseActions.handleSelectTender),
  };
};
